<template>
  <div class="blue-growth-viewer">
    <section v-if="graph.serie.length > 0" id="variables-section">
      <vs-divider position="left" class="viewer-divider">Variable</vs-divider>
      <div class="inner-container">
        <ul class="radio-opts label">
          <li v-for="(product, index) in operativity.products" :key="index">
            <vs-radio
              v-model="activeProduct"
              :color="secondary"
              :vs-value="product"
              >{{ product.variable.alias | capitalizeFirst }}</vs-radio
            >
          </li>
        </ul>
      </div>
    </section>
    <section
      v-if="graph.serie.length > 0"
      id="graph-section"
      class="graph-section"
    >
      <vs-divider class="viewer-divider">Data Serie</vs-divider>
      <line-thresholds-chart
        :z-data-serie="graph.serie"
        :z-variable-info="graph.variable"
        :z-data-source="graph.source"
        :z-data-threshold="graphThresholds"
        :z-subtitle="graph.subtitle"
        :z-title="graph.title"
      />
      <br />
      <label class="footer-label">* Data from {{ activeProduct.alias }}</label>
    </section>
  </div>
</template>

<script>
import {
  getProductDateLimits,
  getProductDataSerie,
  getSpeedFromComponents
} from "@/api/thredds";
import LineChart from "@/components/LineChart";
import { MOMENTS } from "@/constants.js";
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  components: {
    "line-thresholds-chart": LineChart
  },
  filters: {
    capitalizeFirst: value => {
      if (!value) return "";
      return value[0].toUpperCase() + value.substring(1);
    }
  },
  data() {
    return {
      activeProduct: {},
      dates: {},
      graphThresholds: {},
      graph: {
        serie: [],
        source: {},
        thresholds: {}, //{max:0,min:0}
        variable: {},
        title: "",
        subtitle: ""
      },
      secondary: "#19535F",
      moments: MOMENTS
    };
  },
  computed: {
    ...mapState("map", {
      marker: state => state.marker
    }),
    ...mapGetters("map", {
      roundedMarkerCoords: "roundedMarkerCoords"
    }),
    ...mapState({
      operativity: state => state.bluegrowth.operativity
    }),
    ready() {
      return this.operativity.products.length > 0;
    }
  },
  watch: {
    activeProduct() {
      this.setActiveGraphData();
    }
  },
  created() {
    this.toggleLoading(true);
  },
  mounted() {
    if (this.ready) {
      this.activeProduct = this.operativity.products[0]; // init
    }
  },
  methods: {
    ...mapActions({
      toggleLoading: "toggleLoading"
    }),
    setActiveGraphData() {
      this.loadShortTermOperativityData();
    },
    async loadShortTermOperativityData() {
      try {
        this.toggleLoading(true);
        this.dates = await getProductDateLimits(
          this.activeProduct.urlBase,
          this.activeProduct.latest
        );
        const dataSerie = await this.getDataSerie();
        this.setupGraphData(dataSerie);
      } catch (error) {
        this.$_utilsMixin_notify("Error getting forecast data serie", error, 2);
      } finally {
        this.toggleLoading(false);
      }
    },
    async getDataSerie() {
      try {
        if (this.activeProduct.hasComponents === false) {
          const params = this.getParams(this.activeProduct.variable);
          const data = await getProductDataSerie(params);
          return data;
        } else {
          const vectorData = await this.getVectorDataSerie();
          return vectorData;
        }
      } catch (error) {
        this.$_utilsMixin_notify("Error getting data serie", error, 2);
      }
    },
    async getVectorDataSerie() {
      const paramsU = this.getParams(this.activeProduct.u);
      const paramsV = this.getParams(this.activeProduct.v);
      const dataU = await getProductDataSerie(paramsU);
      const dataV = await getProductDataSerie(paramsV);

      var vectorDataSerie = [];
      for (var i = 0; i < dataU.length; i++) {
        vectorDataSerie.push([
          dataU[i][0],
          await getSpeedFromComponents(dataU[i][1], dataV[i][1])
        ]);
      }
      return vectorDataSerie;
    },
    getParams(variable) {
      return {
        baseUrl: this.activeProduct.urlBase,
        latestPath: this.activeProduct.latest,
        variable: variable,
        date: this.dates,
        point: this.marker
      };
    },
    setupGraphData(dataSerie) {
      const variableInfo = {
        alias: this.activeProduct.variable.alias,
        units: this.activeProduct.variable.units
      };
      const source = {
        name: this.activeProduct.source.alias,
        link: this.activeProduct.source.urlEntity
      };

      this.graph.title = `${this.$_utilsMixin_capitalize(
        variableInfo.alias
      )} at ${this.marker.lon}° - ${this.marker.lat}° (Lon-Lat)`;
      this.graph.subtitle = `(in ${variableInfo.units})`;
      this.graph.variable = variableInfo;
      this.graph.source = source;
      this.graph.serie = dataSerie;
      this.graphThresholds = this.activeProduct.variable.thresholds;
    }
  }
};
</script>
<style scoped>
.blue-growth-viewer {
  flex-grow: 1;
  width: 100%;
}
.graph-section {
  padding: 1rem;
}
.viewer-divider {
  padding-left: 1rem;
  padding-right: 1rem;
}
</style>
